import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../cn";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "tonal" | "soft" | "ghost" | "danger" | "danger-solid" | "link";
export type ButtonSize = "sm" | "md" | "lg";

const base =
  "relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-control font-semibold transition-[background-color,color,box-shadow,transform] duration-150 ease-out active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-container";

const variants: Record<ButtonVariant, string> = {
  primary: "bg-primary-container text-on-primary shadow-[0_1px_2px_rgb(92_4_39/0.25)] hover:bg-primary hover:shadow-brand",
  secondary:
    "bg-surface-container-lowest text-on-surface ring-1 ring-inset ring-outline-variant/70 shadow-card hover:bg-surface-container-low hover:ring-outline/50",
  tonal: "bg-surface-container text-on-surface hover:bg-surface-container-high",
  soft: "bg-primary-fixed/70 text-primary hover:bg-primary-fixed",
  ghost: "text-primary hover:bg-primary-soft",
  danger: "text-error hover:bg-error-container/50",
  "danger-solid": "bg-error text-on-error hover:bg-on-error-container",
  link: "h-auto px-0 text-primary underline-offset-4 hover:underline active:scale-100",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-label-md md:h-8 [&_svg]:size-4",
  md: "h-11 px-4 text-label-lg md:h-10 [&_svg]:size-[18px]",
  lg: "h-12 px-5 text-label-lg [&_svg]:size-5",
};

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
}

/** Use on links that should look like buttons. */
export function buttonClasses({ variant = "primary", size = "md", block, className }: ButtonClassOptions = {}): string {
  return cn(base, variants[variant], variant === "link" ? "h-auto" : sizes[size], block && "w-full", className);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", block, loading, leading, trailing, className, children, disabled, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClasses({ variant, size, block, className })}
      {...props}
    >
      {loading ? <Spinner className="size-4" /> : leading}
      {children}
      {!loading && trailing}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: "ghost" | "tonal" | "secondary" | "primary" | "danger";
  size?: "sm" | "md" | "lg";
}

const iconSizes = {
  sm: "size-9 md:size-8 [&_svg]:size-4",
  md: "size-11 md:size-10 [&_svg]:size-5",
  lg: "size-12 [&_svg]:size-6",
};

const iconVariants = {
  ghost: "text-on-surface-variant hover:bg-surface-container hover:text-on-surface",
  tonal: "bg-surface-container text-on-surface hover:bg-surface-container-high",
  secondary: "bg-surface-container-lowest text-on-surface ring-1 ring-inset ring-outline-variant/70 hover:bg-surface-container-low",
  primary: "bg-primary-container text-on-primary hover:bg-primary",
  danger: "text-on-surface-variant hover:bg-error-container/50 hover:text-error",
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = "ghost", size = "md", className, children, type = "button", ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center rounded-control transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-container",
        iconSizes[size],
        iconVariants[variant],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});
